import api from './api';
import type { ChassisCompatibilityMatrix } from '../utils/chassisValidation';

/**
 * Size <-> chassis-type compatibility matrix fetcher.
 *
 * The CANONICAL source is the backend (app/core/chassis_compatibility.py),
 * exposed at GET /chassis/size-compatibility, and also consumed by the saverule
 * order_equipsize — so the saverule and this frontend validation share one
 * definition and can't silently drift. (The v1 mistake this replaces was
 * sr_equipsize and lib_editrecord.pl encoding the same matrix separately and
 * disagreeing on STANDARD+48.)
 *
 * The bundled bootstrap/fallback (DEFAULT_CHASSIS_MATRIX) lives next to the
 * validator in utils/chassisValidation; this module just fetches the live copy
 * and caches it. The fetched matrix OVERRIDES the default at runtime, so a
 * backend change is picked up without a frontend redeploy.
 */
export type { ChassisCompatibilityMatrix, ChassisSizeRule } from '../utils/chassisValidation';
export { DEFAULT_CHASSIS_MATRIX } from '../utils/chassisValidation';

/** Session-scoped cache (small, static within a session). */
let matrixCache: Promise<ChassisCompatibilityMatrix> | null = null;

/**
 * Fetches the canonical matrix from the backend. Cached for the session;
 * resets the cache on failure so a later attempt can retry.
 */
export async function getChassisCompatibility(): Promise<ChassisCompatibilityMatrix> {
  if (!matrixCache) {
    matrixCache = api
      .get<{ matrix: ChassisCompatibilityMatrix }>('/chassis/size-compatibility')
      .then((r) => r.data.matrix)
      .catch((err) => {
        matrixCache = null;
        throw err;
      });
  }
  return matrixCache;
}

/** Drops the in-memory cache. Useful for tests. */
export function clearChassisCompatibilityCache(): void {
  matrixCache = null;
}
